import { hooks } from "@springtree/eva-sdk-react-recoil";
import { Core } from "@springtree/eva-services-core";
import classNames from "classnames";
import { HTTPError } from "ky";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { SubmitHandler } from "react-hook-form";

import myaccount from "~/components/common/myaccount.module.scss";
import { useWishlist } from "~/contexts/wishlist";
import { wishlistNameFields } from "~/shared/wishlist/wishlist.model";

import Button from "../../common/button";
import Icon from "../../common/icon";
import LinkWrapper from "../../common/link-wrapper";
import CreateWishlistDialog from "./create-wishlist-dialog";
import DeleteWishlistDialog from "./delete-wishlist-dialog";
import EditWishlistDialog from "./edit-wishlist-dialog";
import styles from "./wishlist.module.scss";

export default function Wishlist() {
  const t = useTranslations();

  const { list, refetch, updateWishlist, deleteWishlist } = useWishlist();

  const createWishlist = hooks.useCallService({ service: Core.CreateUserWishlist });

  const [isEditWishlistOpen, setIsEditWishlistOpen] = useState(false);
  const [isCreateWishlistOpen, setIsCreateWishlistOpen] = useState(false);
  const [isDeleteWishlistOpen, setIsDeleteWishlistOpen] = useState(false);
  const [wishlistToDelete, setWishlistToDelete] = useState<number>();
  const [wishlistToEdit, setWishlistToEdit] = useState({ name: "", id: 0 });
  const [wishListCreateError, setWishListCreateError] = useState<any>();
  const [wishListUpdateError, setWishListUpdateError] = useState<any>();
  const [wishListDeleteError, setWishListDeleteError] = useState<any>();

  const createWishlistHandler = async (data: { wishlistName: string }) => {
    setWishListCreateError(undefined);

    await createWishlist(
      { Name: data.wishlistName },
      {},
      {
        onError: async (error) => {
          if (error.name === "HTTPError") {
            const jsonError = await (error as HTTPError).response.json();
            setWishListCreateError(jsonError);
          }
        },
        onSuccess: async (response: EVA.Core.CreateUserWishlistResponse | undefined) => {
          setWishListCreateError(undefined);
          setIsCreateWishlistOpen(false);
          refetch();
        },
      }
    );
  };

  const deleteWishlistHandler = async (confirm?: boolean) => {
    if (confirm && wishlistToDelete) {
      await deleteWishlist({
        wishlistId: `${wishlistToDelete}`,
        onSuccess: async () => {
          setWishListDeleteError(undefined);
          setIsDeleteWishlistOpen(false);
          refetch();
        },
        onError: async (error) => {
          if (error?.name === "HTTPError") {
            const jsonError = await (error as HTTPError).response.json();
            setWishListDeleteError(jsonError);
          }
        },
      });
    } else {
      setIsDeleteWishlistOpen(false);
    }
  };

  const updateWishlistHandler: SubmitHandler<wishlistNameFields> = async (submitData: wishlistNameFields) => {
    setWishListUpdateError(undefined);

    await updateWishlist({
      wishlistName: submitData.wishlistName,
      wishlistId: `${wishlistToEdit.id}`,
      onSuccess: async () => {
        setWishListUpdateError(undefined);
        setIsEditWishlistOpen(false);
        refetch();
      },
      onError: async (error) => {
        if (error?.name === "HTTPError") {
          const jsonError = await (error as HTTPError).response.json();
          setWishListUpdateError(jsonError);
        }
      },
    });
  };

  return (
    <>
      <div className={styles.wishlistItemsList}>
        {list &&
          list?.map((el) => {
            return (
              <div key={el.ID}>
                <li className={classNames(myaccount.myaccountCard, styles.wishlistItem)}>
                  <div className={styles.wishlistWrapperItem}>
                    <h2>
                      <LinkWrapper href={`wishlist/${el.ID}`} className={styles.wishlistItemName}>
                        {el.Name} ({el?.count})
                      </LinkWrapper>
                    </h2>
                    <Button
                      lookAsLink
                      onClick={(e) => {
                        e.preventDefault();
                        setWishlistToEdit({ name: el.Name, id: el.ID });
                        setIsEditWishlistOpen(true);
                      }}
                    >
                      {t("generic.edit")}
                    </Button>
                    <Button
                      lookAsLink
                      onClick={(e) => {
                        e.preventDefault();
                        setIsDeleteWishlistOpen(true);
                        setWishlistToDelete(el.ID);
                      }}
                    >
                      {t("generic.wishlist.delete")}
                    </Button>
                  </div>
                  <Icon name={"chevron-right"} aria-hidden width={16} height={16} className={styles.paymentCardIcon} />
                </li>
              </div>
            );
          })}
      </div>
      <Button
        className={styles.createWs}
        variant="secondary"
        onClick={() => setIsCreateWishlistOpen(true)}
        type="button"
      >
        {t("generic.myaccount.wishlist.create_cta")}
      </Button>
      {wishlistToEdit.name && (
        <EditWishlistDialog
          isOpen={isEditWishlistOpen}
          onClose={() => setIsEditWishlistOpen(false)}
          error={wishListUpdateError}
          cleanError={() => setWishListUpdateError(undefined)}
          defaultName={wishlistToEdit.name}
          submit={updateWishlistHandler}
        />
      )}
      <CreateWishlistDialog
        isOpen={isCreateWishlistOpen}
        onClose={() => setIsCreateWishlistOpen(false)}
        error={wishListCreateError}
        cleanError={() => setWishListCreateError(undefined)}
        submit={createWishlistHandler}
      />
      <DeleteWishlistDialog
        isOpen={isDeleteWishlistOpen}
        onClose={() => setIsDeleteWishlistOpen(false)}
        error={wishListDeleteError}
        cleanError={() => setWishListDeleteError(undefined)}
        onSubmit={deleteWishlistHandler}
      />
    </>
  );
}
